import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBookings } from '../api';
import { useScope } from '../../store/scope';
import { PageLoader, ErrorState, Pagination, StatusBadge, EmptyState } from '../../components/ui';
import { money, dateTime, num, weight } from '../../lib/format';
import type { BookingStatus } from '../../lib/types';

const STATUSES: BookingStatus[] = ['New', 'Contacted', 'Confirmed', 'Processing', 'Completed', 'Cancelled'];

export function BookingsPage() {
  const { participationId } = useScope();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading, error, refetch } = useBookings({
    page,
    limit: 25,
    participationId: participationId || undefined,
    status: status || undefined,
    search: search || undefined,
  });

  const rows = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-ink-900">Bookings</h1>
        <p className="text-sm text-ink-400">
          {data?.meta ? `${num(data.meta.total)} bookings` : ' '}
          {participationId ? ' · filtered to selected expo' : ''}
        </p>
      </div>

      <div className="card flex flex-wrap gap-2 p-3">
        <input
          className="input max-w-xs"
          placeholder="Booking # / name / mobile…"
          value={search}
          onChange={(e) => { setPage(1); setSearch(e.target.value); }}
        />
        <select className="input w-auto" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }}>
          <option value="">All status</option>
          {STATUSES.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : error ? (
        <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title="No bookings yet" hint="Bookings placed from a rack QR appear here." />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="table-head">
                  <th className="px-3 py-2">Booking #</th>
                  <th className="px-3 py-2">Placed</th>
                  <th className="px-3 py-2">Visitor</th>
                  <th className="px-3 py-2">Mobile</th>
                  <th className="px-3 py-2 text-right">Items</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Gold weight</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {rows.map((b) => (
                  <tr key={b._id} className="hover:bg-ink-50/60">
                    <td className="px-3 py-2 font-mono">{b.bookingNumber}</td>
                    <td className="px-3 py-2 text-xs text-ink-400">{dateTime(b.createdAt)}</td>
                    <td className="px-3 py-2">{b.visitorName}</td>
                    <td className="px-3 py-2">{b.visitorMobile}</td>
                    <td className="px-3 py-2 text-right">{b.items.length}</td>
                    <td className="px-3 py-2 text-right">{b.totalQuantity}</td>
                    <td className="px-3 py-2 text-right text-ink-500">{weight(b.totalGrossWeight)}</td>
                    <td className="px-3 py-2 text-right font-medium">{money(b.totalAmount)}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={b.status} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link className="btn-ghost" to={`/admin/bookings/${b._id}`}>
                        Open
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3">
            <Pagination page={page} pages={data?.meta?.pages ?? 1} onPage={setPage} />
          </div>
        </div>
      )}
    </div>
  );
}
